import React from "react";

import { useParams } from "react-router-dom";

import { useProject } from "../../../../../hooks/useProject";
import {
  type ProjectInvitation,
  type ProjectInvitationID,
  ProjectUserAssignableRoles,
  useProjectUserRoleDisplayName,
  useUpdateProjectInvitation,
} from "../../../../../hooks/useProjectSharing";
import { useTeamActiveSubscription } from "../../../../../hooks/useTeamActiveSubscription";
import { useTeam } from "../../../../../hooks/useTeams";
import { useTracker } from "../../../../../hooks/useTracker";
import { useShareBadge } from "./ShareBadge";
import { ShareRow, type ShareRowRef } from "./ShareRow";

interface InvitationRowProps {
  invitation: ProjectInvitation;
  onRemove?: (invitationId: ProjectInvitationID) => void;
  containerIntersectionObserver?: IntersectionObserver | null;
  showPaywall?: () => void;
}

export const InvitationRowProject = React.forwardRef<ShareRowRef, InvitationRowProps>(
  ({ invitation, onRemove, containerIntersectionObserver, showPaywall }, ref) => {
    const {
      mutate: mutateUpdateProjectInvitation,
      isPending: isUpdatePending,
      isError: isUpdateError,
    } = useUpdateProjectInvitation({});
    const tracker = useTracker();
    const projectUserRoleDisplayName = useProjectUserRoleDisplayName();
    const shareBadge = useShareBadge();
    const { projectId = "" } = useParams();

    const { data: projectData } = useProject();
    const { teamData } = useTeam(projectData?.teamId);

    const { data: isTeamSubscribed, isPending: isSubscriptionDataPending } =
      useTeamActiveSubscription(projectData?.teamId);

    return (
      <ShareRow
        ref={ref}
        roles={ProjectUserAssignableRoles}
        proRoles={new Set<(typeof ProjectUserAssignableRoles)[number]>(["editor"])}
        showPaywall={showPaywall}
        isProRoleEligible={isTeamSubscribed ?? false}
        roleDisplayName={projectUserRoleDisplayName}
        entity={invitation}
        containerIntersectionObserver={containerIntersectionObserver}
        onRemove={() => {
          onRemove?.(invitation.invitationId);
          tracker.trackShareModalMemberRemoveIntended({
            remoteProjectId: projectId,
            invitationId: invitation.invitationId,
            sharedResource: "project",
            spaceId: projectData?.space?.spaceId,
            teamUserRole: teamData?.role,
            projectUserRole: projectData?.userRole,
          });
        }}
        onUpdate={(prevRole, newRole) => {
          tracker.trackShareModalMemberRoleChanged({
            remoteProjectId: projectId,
            invitationId: invitation.invitationId,
            oldValue: prevRole,
            newValue: newRole,
            sharedResource: "project",
            spaceId: projectData?.space?.spaceId,
            teamUserRole: teamData?.role,
            projectUserRole: projectData?.userRole,
          });
          mutateUpdateProjectInvitation({
            projectInvitationId: invitation.invitationId,
            role: newRole,
          });
        }}
        isPending={isUpdatePending || isSubscriptionDataPending}
        isError={isUpdateError}
        sharingBadges={[shareBadge("invited")]}
      />
    );
  }
);
